import { useCallback, useEffect, useRef, useState } from "react";
import { queryOptions, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useCompanyList } from "@/features/company";
import type { DispatchCompanyListItem } from "@/features/company";
import {
  getCompanyStatsSummary,
  getCompanyStatsTrend,
} from "../services/orderAnalyticsService";
import { useOrderAnalyticsStore } from "../stores/orderAnalyticsStore";
import { analyticsRequestQueue } from "../utils/requestQueue";
import type {
  AnalyticsPeriod,
  FetchAllProgress,
  PeriodPresetKey,
  TrendRange,
} from "../types/orderAnalytics.types";

const SUMMARY_KEY = ["orderAnalytics", "summary"] as const;
const TREND_KEY = ["orderAnalytics", "trend"] as const;

/** 카드에 올릴 업체 수 상한. 넘으면 화면에서 안내한다. */
export const ANALYTICS_COMPANY_LIMIT = 500;

const COMPANY_LIST_PARAMS = {
  page: 1,
  pageSize: ANALYTICS_COMPANY_LIMIT,
  searchStr: "",
  isActive: null,
};

/** 업체 기본 정보만 가져온다. 분석 쿼리는 여기서 나가지 않는다. */
export const useAnalyticsCompanies = () => useCompanyList(COMPANY_LIST_PARAMS);

const summaryQueryOptions = (companyGuid: string, period: AnalyticsPeriod) =>
  queryOptions({
    queryKey: [...SUMMARY_KEY, companyGuid, period.startDate, period.endDate],
    queryFn: ({ signal }) =>
      analyticsRequestQueue.enqueue(
        (s) => getCompanyStatsSummary(companyGuid, period, s),
        { signal },
      ),
    // 같은 기간으로 이미 조회한 카드는 다시 눌러도 요청하지 않는다. 재조회는 새로고침 버튼으로만.
    staleTime: Infinity,
    retry: false,
  });

/**
 * 카드가 구독하는 요약 상태. enabled: false 라 스스로는 절대 조회하지 않고,
 * useSummaryFetcher 가 채운 캐시를 읽기만 한다.
 */
export const useCompanySummary = (companyGuid: string) => {
  const period = useOrderAnalyticsStore((s) => s.period);
  return useQuery({ ...summaryQueryOptions(companyGuid, period), enabled: false });
};

export const useSummaryFetcher = () => {
  const qc = useQueryClient();

  const fetchSummary = useCallback(
    (companyGuid: string, period: AnalyticsPeriod) =>
      qc.fetchQuery(summaryQueryOptions(companyGuid, period)),
    [qc],
  );

  const refreshSummary = useCallback(
    async (companyGuid: string, period: AnalyticsPeriod) => {
      const options = summaryQueryOptions(companyGuid, period);
      await qc.invalidateQueries({
        queryKey: options.queryKey,
        refetchType: "none",
      });
      return qc.fetchQuery(options);
    },
    [qc],
  );

  return { fetchSummary, refreshSummary };
};

/**
 * 기간 변경. 자동 재조회는 하지 않고, 이미 조회된 카드를 미조회 상태로 되돌린다.
 */
export const usePeriodControls = () => {
  const qc = useQueryClient();

  const resetSummaries = useCallback(() => {
    void qc.cancelQueries({ queryKey: SUMMARY_KEY });
    qc.removeQueries({ queryKey: SUMMARY_KEY });
  }, [qc]);

  const applyPreset = useCallback(
    (key: PeriodPresetKey) => {
      useOrderAnalyticsStore.getState().applyPreset(key);
      resetSummaries();
    },
    [resetSummaries],
  );

  const changePeriod = useCallback(
    (period: AnalyticsPeriod) => {
      useOrderAnalyticsStore.getState().setPeriod(period);
      resetSummaries();
    },
    [resetSummaries],
  );

  return { applyPreset, changePeriod };
};

/**
 * 모두 가져오기. 업체를 한 번에 하나씩 순차 조회한다 — 앞 요청이 끝난(성공·실패) 뒤에만 다음 요청을 시작한다.
 * 실패한 업체는 오류 상태로 남기고 다음 업체로 넘어간다.
 */
export const useFetchAllSummaries = (companies: DispatchCompanyListItem[]) => {
  const qc = useQueryClient();
  const { fetchSummary } = useSummaryFetcher();
  const [progress, setProgress] = useState<FetchAllProgress | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const start = useCallback(async () => {
    if (abortRef.current) return;
    const controller = new AbortController();
    abortRef.current = controller;

    const period = useOrderAnalyticsStore.getState().period;
    const total = companies.length;
    let done = 0;
    let failed = 0;

    try {
      for (const company of companies) {
        if (controller.signal.aborted) break;
        setProgress({ total, done, failed, currentName: company.companyName });
        try {
          await fetchSummary(company.companyGuid, period);
        } catch {
          if (controller.signal.aborted) break;
          failed += 1;
        }
        done += 1;
      }
    } finally {
      abortRef.current = null;
      setProgress(null);
    }

    if (controller.signal.aborted) {
      toast.info(`조회를 중단했습니다. (${done}/${total})`);
    } else if (failed > 0) {
      toast.warning(`${total}개 업체 중 ${failed}개 조회에 실패했습니다.`);
    } else {
      toast.success(`${total}개 업체 조회를 마쳤습니다.`);
    }
  }, [companies, fetchSummary]);

  const abort = useCallback(() => {
    if (!abortRef.current) return;
    abortRef.current.abort();
    void qc.cancelQueries({ queryKey: SUMMARY_KEY });
  }, [qc]);

  useEffect(() => abort, [abort]);

  return { progress, isRunning: progress !== null, start, abort };
};

/**
 * 팝업 경향. 선택한 탭의 기간만 그때 조회하고, 같은 팝업 안에서는 탭을 다시 눌러도 재조회하지 않는다.
 * 모두 가져오기와 같은 직렬 큐를 타되, 사용자가 기다리는 요청이라 대기열 맨 앞에 넣는다.
 */
export const useCompanyTrend = (companyGuid: string | null, range: TrendRange) =>
  useQuery({
    queryKey: [...TREND_KEY, companyGuid, range],
    queryFn: ({ signal }) =>
      analyticsRequestQueue.enqueue(
        (s) => getCompanyStatsTrend(companyGuid!, range, s),
        { signal, priority: true },
      ),
    enabled: !!companyGuid,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
  });

export const useTrendActions = () => {
  const qc = useQueryClient();

  const retryTrend = useCallback(
    (companyGuid: string, range: TrendRange) =>
      qc.invalidateQueries({ queryKey: [...TREND_KEY, companyGuid, range] }),
    [qc],
  );

  /** 팝업을 닫을 때 호출 — 다음에 열면 다시 조회한다. */
  const clearTrends = useCallback(() => {
    void qc.cancelQueries({ queryKey: TREND_KEY });
    qc.removeQueries({ queryKey: TREND_KEY });
  }, [qc]);

  return { retryTrend, clearTrends };
};
